'use client'
import React, { createContext, useContext, useState, useEffect } from 'react'
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { amber, green, grey, indigo, red } from '@mui/material/colors'

type ThemeMode = 'light' | 'dark'

interface ThemeContextType {
  mode: ThemeMode
  toggleTheme: () => void
  lightPalette: any
  darkPalette: any
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useCustomTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useCustomTheme must be used within a ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: React.ReactNode
}

export default function ThemeProvider({ children }: ThemeProviderProps) {
  const [mode, setMode] = useState<ThemeMode>('light')
  const [mounted, setMounted] = useState(false)

  // Load theme preference from localStorage on mount
  useEffect(() => {
    try {
      setMounted(true)
      const savedTheme = localStorage.getItem('theme') as ThemeMode
      if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
        setMode(savedTheme)
      } else {
        // Check system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        setMode(prefersDark ? 'dark' : 'light')
      }
    } catch (error) {
      console.error('Error loading theme preference:', error)
      // Fallback to system preference
      try {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        setMode(prefersDark ? 'dark' : 'light')
      } catch (fallbackError) {
        console.error('Error detecting system preference:', fallbackError)
        setMode('light') // Final fallback
      }
    }
  }, [])

  // Save theme preference to localStorage when it changes
  useEffect(() => {
    if (mounted) {
      try {
        localStorage.setItem('theme', mode)
      } catch (error) {
        console.error('Error saving theme preference:', error)
      }
    }
  }, [mode, mounted])

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'))
  }

  // Define light mode palette
  const darkPalette = {
    primary: {
      main: grey[900],
      contrastText: grey[50],
    },
    secondary: {
      main: grey[700],
      contrastText: grey[200],
    },
    background: {
      default: grey[100],
      paper: grey[100],
    },
    text: {
      primary: grey[900],
      secondary: grey[800],
    },
    error: {
      main: red[500],
      contrastText: red[50],
    },
    warning: {
      main: amber[500],
      contrastText: amber[50],
    },
    info: {
      main: indigo[500],
      contrastText: indigo[50],
    },
    success: {
      main: green[500],
      contrastText: green[50],
    },
    divider: '#e0e0e0',
    action: {
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
    },
    customColors: {
      accent: '#ff6b6b',
      highlight: '#4ecdc4',
      muted: '#95a5a6',
      surface: '#f8f9fa',
    },
  }

  // Define dark mode palette
  const lightPalette = {
    primary: {
      main: grey[50],
      contrastText: grey[900],
    },
    secondary: {
      main: grey[200],
      contrastText: grey[700],
    },
    background: {
      default: grey[800],
      paper: grey[900],
    },
    text: {
      primary: grey[50],
      secondary: grey[200],
    },
    error: {
      main: red[400],
      contrastText: red[900],
    },
    warning: {
      main: amber[400],
      contrastText: amber[900],
    },
    info: {
      main: indigo[200],
      contrastText: indigo[900],
    },
    success: {
      main: green[400],
      contrastText: green[900],
    },
    divider: '#424242',
    action: {
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.12)',
      disabled: 'rgba(255, 255, 255, 0.26)',
    },
    customColors: {
      accent: '#ff7675',
      highlight: '#00b894',
      muted: '#636e72',
      surface: '#2d3748',
    },
  }

  const theme = createTheme({
    palette: {
      mode,
      ...(mode === 'light' ? lightPalette : darkPalette),
    },
    typography: {
      fontFamily: 'Roboto, Arial, sans-serif', // Default for body text
      // Common settings for all typography
      allVariants: {
        letterSpacing: 0.2,
      },
      h1: {
        fontFamily: '"Arial", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 700,
        fontSize: '1.75rem',
        lineHeight: 1.2,
        '@media (min-width:600px)': {
          fontSize: '2rem',
        },
        '@media (min-width:960px)': {
          fontSize: '2.5rem',
        },
        '@media (min-width:1280px)': {
          fontSize: '3rem',
        },
      },
      h2: {
        fontFamily: '"Arial", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 700,
        fontSize: '1.5rem',
        lineHeight: 1.3,
        '@media (min-width:600px)': {
          fontSize: '1.75rem',
        },
        '@media (min-width:960px)': {
          fontSize: '2rem',
        },
        '@media (min-width:1280px)': {
          fontSize: '2.5rem',
        },
      },
      h3: {
        fontFamily: '"Arial", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.3,
        '@media (min-width:600px)': {
          fontSize: '1.5rem',
        },
        '@media (min-width:960px)': {
          fontSize: '1.75rem',
        },
        '@media (min-width:1280px)': {
          fontSize: '2rem',
        },
      },
      h4: {
        fontFamily: '"Arial", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 600,
        fontSize: '1.125rem',
        lineHeight: 1.4,
        '@media (min-width:600px)': {
          fontSize: '1.25rem',
        },
        '@media (min-width:960px)': {
          fontSize: '1.5rem',
        },
        '@media (min-width:1280px)': {
          fontSize: '1.75rem',
        },
      },
      h5: {
        fontFamily: '"Arial", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 1.4,
        '@media (min-width:600px)': {
          fontSize: '1.125rem',
        },
        '@media (min-width:960px)': {
          fontSize: '1.25rem',
        },
        '@media (min-width:1280px)': {
          fontSize: '1.5rem',
        },
      },
      h6: {
        fontFamily: '"Arial", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: 1.5,
        '@media (min-width:600px)': {
          fontSize: '1rem',
        },
        '@media (min-width:960px)': {
          fontSize: '1.125rem',
        },
        '@media (min-width:1280px)': {
          fontSize: '1.25rem',
        },
      },
      subtitle1: {
        fontWeight: 500,
        fontSize: '0.875rem',
        lineHeight: 1.5,
        '@media (min-width:600px)': {
          fontSize: '1rem',
        },
        '@media (min-width:960px)': {
          fontSize: '1.125rem',
        },
      },
      subtitle2: {
        fontWeight: 500,
        fontSize: '0.8125rem',
        lineHeight: 1.5,
        '@media (min-width:600px)': {
          fontSize: '0.875rem',
        },
        '@media (min-width:960px)': {
          fontSize: '1rem',
        },
      },
      body1: {
        // fontFamily: '"Times New Roman", Times, serif',
        fontSize: '0.875rem',
        lineHeight: 1.6,
        '@media (min-width:600px)': {
          fontSize: '1rem',
        },
        '@media (min-width:960px)': {
          fontSize: '1.125rem',
        },
        '@media (min-width:1280px)': {
          fontSize: '1.25rem',
        },
      },
      body2: {
        // fontFamily: '"Times New Roman", Times, serif',
        // fontSize: '0.75rem',
        lineHeight: 1.6,
        '@media (min-width:600px)': {
          fontSize: '1rem',
        },
        '@media (min-width:960px)': {
          fontSize: '1rem',
        },
        '@media (min-width:1280px)': {
          fontSize: '1.125rem',
        },
      },
      caption: {
        fontFamily: '"Times New Roman", Times, serif',
        fontSize: '0.75rem',
        lineHeight: 1.4,
        fontStyle: 'italic',
      },
      button: {
        fontFamily: '"Arial", "Helvetica Neue", Arial, sans-serif',
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 8,
    },
    spacing: 8,
    cssVariables: true,
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'box-shadow 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        },
      },
    },
  })

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, lightPalette, darkPalette }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
